import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldOff } from 'lucide-react';

/**
 * AdminRoute — schützt Routen die nur für SUPER_ADMIN zugänglich sind.
 * Zwei Modi:
 *   <AdminRoute>          → redirect zu /dashboard wenn nicht SUPER_ADMIN
 *   <AdminOnly>           → rendert null (unsichtbar) wenn nicht SUPER_ADMIN
 */

// ── Route Guard (für React Router) ────────────────────────────────────────────
const AdminRoute = ({ children, requiredRole = 'SUPER_ADMIN', redirectTo = '/dashboard' }) => {
  const { isLoading, isAuthenticated, isSuperAdmin, isAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Berechtigungen werden geprüft…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const hasAccess =
    requiredRole === 'SUPER_ADMIN' ? isSuperAdmin :
    requiredRole === 'ADMIN'       ? (isSuperAdmin || isAdmin) :
    false;

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="text-red-400" size={28} />
          </div>
          <h2 className="text-white text-xl font-semibold mb-2">Kein Zugriff</h2>
          <p className="text-gray-400 text-sm mb-6">
            Dieser Bereich ist nur für Administratoren zugänglich.
          </p>
          <a
            href="/dashboard"
            className="inline-block bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            Zum Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
};

// ── Inline Guard (für UI-Elemente) ─────────────────────────────────────────────
export const AdminOnly = ({ children, requiredRole = 'SUPER_ADMIN' }) => {
  const { isSuperAdmin, isAdmin } = useAuth();

  const hasAccess =
    requiredRole === 'SUPER_ADMIN' ? isSuperAdmin :
    requiredRole === 'ADMIN'       ? (isSuperAdmin || isAdmin) :
    false;

  if (!hasAccess) return null;
  return children;
};

export default AdminRoute;
